import { useState } from 'react';
import { iconePara } from '../assets/items';
import { efeitoDe, estadoDaMagia, itensDoGrimorio, LIVRO, tipoDaMagia, type Efeito, type ItemDoGrimorio } from '../data/grimorio';
import { acaoDoSlot } from '../data/hotbar';
import { ehPocaoVip } from '../data/consumiveis';
import { ehMagiaPronta, iconeDaMagia, type Magia } from '../data/magias';
import { numero, t, umaCasa } from '../i18n/idioma';
import { nomeDaMagia, nomeDaSkill, nomeDoItem } from '../i18n/nomes';
import { rotuloCurto, teclaDe } from '../input/atalhos';
import { useAtalhos } from '../input/useAtalhos';
import { GumpWindow } from './GumpWindow';
import { IcCadeado, IcMagia } from './icones/IconesV3';
import { useCompacto } from './hud/useModoTela';
import { useDica } from './hud/Dica';
import { DicaMagia, argsDoEfeito } from './hud/dicasConteudo';
import { useSkillsMagia } from '../world/skillsMagia';

type Aba = 'magias' | 'itens';

interface SpellPickerProps {
  /** Quem recebe a escolha — o slot clicado, mesmo vazio. */
  slot: number;
  barra: readonly (string | null)[];
  /** `null` = o perfil não chegou (ou falhou): sem a barrinha de chance. */
  magery: number | null;
  velMagia: number;
  consumiveis?: Readonly<Record<string, number>>;
  /** VIP ativo: as poções da barra sem pilha mostram ∞ (pagas pelo banco). */
  vip?: boolean;
  onPick(id: string): void;
  /** Limpar o slot (só aparece quando ele tem algo). */
  onLimpar?(): void;
  onClose(): void;
}

function textoDoEfeito(e: Efeito | null): string | null {
  if (!e) return null;
  switch (e.tipo) {
    case 'dano': return e.atrasoMs > 0
      ? t('grimorio.efeito.danoAtraso', { min: e.min, max: e.max, s: umaCasa(e.atrasoMs / 1000) })
      : t('grimorio.efeito.dano', { min: e.min, max: e.max });
    case 'mente': return t('grimorio.efeito.mente');
    case 'veneno': return t('grimorio.efeito.veneno');
    case 'antidoto': return t('grimorio.efeito.antidoto');
    case 'cura': return t('grimorio.efeito.cura');
    case 'recall': return t('grimorio.efeito.recall');
    case 'paralisia': return t('grimorio.efeito.paralisia');
    case 'efeito': return t(`grimorio.efeito.${e.id}`, argsDoEfeito(e.id));
  }
}

const corChance = (c: number) => (c >= 0.7 ? '#5cbf3c' : c >= 0.35 ? '#e6c452' : '#d2453b');

/**
 * Escolher magia ou poção para um slot (techspec-hud-v3 §07): a grade do protótipo. Abas Magias e
 * Itens; Ataque e Cura em grupos; mana no canto, barrinha de chance, trancada com cadeado e a Magia
 * mínima; itens com a quantidade (ou ∞ do VIP); a tecla de quem já está na barra; Limpar.
 * A troca de lugar continua sendo o `colocarNaBarra` de quem chama.
 */
export function SpellPicker({ slot, barra, magery, velMagia, consumiveis, vip, onPick, onLimpar, onClose }: SpellPickerProps) {
  useAtalhos();                                      // o rótulo das teclas segue o remapeamento
  const compacto = useCompacto();
  const atual = barra[slot] ?? null;
  const itens = itensDoGrimorio(consumiveis);
  const ehItem = !!atual && itens.some(i => i.id === atual);
  const [aba, setAba] = useState<Aba>(ehItem ? 'itens' : 'magias');
  const tecla = (i: number) => rotuloCurto(teclaDe(acaoDoSlot(i)));
  const ja = (id: string) => {
    const k = barra.indexOf(id);
    return k > 0 && k !== slot ? tecla(k) : '';
  };

  const prontas = LIVRO.filter(m => ehMagiaPronta(m.id));
  const grupos: [string, Magia[]][] = [
    // A ordem (Renato, 28/10/2026): Ataque, Cura, Buff, Debuff.
    [t('grimorio.filtro.ataque'), prontas.filter(m => tipoDaMagia(m) === 'ataque')],
    [t('grimorio.filtro.cura'), prontas.filter(m => tipoDaMagia(m) === 'cura')],
    [t('grimorio.filtro.buff'), prontas.filter(m => tipoDaMagia(m) === 'buff')],
    [t('grimorio.filtro.debuff'), prontas.filter(m => tipoDaMagia(m) === 'debuff')],
    [t('grimorio.filtro.viagem'), prontas.filter(m => tipoDaMagia(m) === 'viagem')]
  ].filter(([, ms]) => (ms as Magia[]).length > 0) as [string, Magia[]][];
  const usaveis = magery == null ? prontas.length : prontas.filter(m => estadoDaMagia(m, magery).estado !== 'longe').length;
  const skill = nomeDaSkill('magery');

  return (
    <GumpWindow
      title={t('pk.titulo', { tecla: tecla(slot) })}
      anchor={{}}
      className="pop pk win--picker"
      comeSlots
      onClose={onClose}
    >
      <div className="pk-topo">
        <div className="pk-abas" role="tablist" aria-label={t('grimorio.abas')}>
          <button type="button" role="tab" aria-selected={aba === 'magias'} aria-pressed={aba === 'magias'} onClick={() => setAba('magias')}>
            {t('grimorio.abaMagias')}<small>{numero(usaveis)}</small>
          </button>
          <button type="button" role="tab" aria-selected={aba === 'itens'} aria-pressed={aba === 'itens'} onClick={() => setAba('itens')}>
            {t('magiaUi.abaItens')}<small>{numero(itens.length)}</small>
          </button>
        </div>
        {aba === 'magias' && magery != null && (
          <span className="pk-sk" title={t('pk.skDica')}><IcMagia />{skill} {umaCasa(magery)}</span>
        )}
      </div>
      {aba === 'magias'
        ? grupos.map(([titulo, ms]) => (
          <div className="pk-gr" key={titulo}>
            <h4 className="ch-sec">{titulo}</h4>
            <div className="pk-g pk-g--magias">
              {ms.map(m => <TileMagia key={m.id} magia={m} magery={magery} velMagia={velMagia} ja={ja(m.id)} atual={m.id === atual} onPick={onPick} />)}
            </div>
          </div>
        ))
        : (
          <div className="pk-gr">
            <h4 className="ch-sec">{t('pk.itens')}</h4>
            <div className="pk-g">
              {itens.map(i => <TileItem key={i.id} item={i} vip={!!vip && ehPocaoVip(i.id)} ja={ja(i.id)} atual={i.id === atual} onPick={onPick} />)}
            </div>
          </div>
        )}
      <div className="pk-pe">
        <span>{compacto ? t('pk.peCel') : t('pk.pePc')}</span>
        {atual && onLimpar && (
          <button type="button" className="btn btn--ghost btn--tiny" onClick={onLimpar}>{t('pk.limpar', { tecla: tecla(slot) })}</button>
        )}
      </div>
    </GumpWindow>
  );
}

function TileMagia({ magia, magery, velMagia, ja, atual, onPick }: { magia: Magia; magery: number | null; velMagia: number; ja: string; atual: boolean; onPick(id: string): void }) {
  const { estado, chance } = estadoDaMagia(magia, magery);
  const skills = useSkillsMagia();
  // A caixa de dica (techspec-tooltips-e-efeitos): mouse, teclado e o toque longo no celular.
  const dica = useDica(() => (
    <DicaMagia id={magia.id} magery={magery ?? skills.magery} evalInt={skills.evalInt} velMagia={velMagia}
      nota={ja ? t('grimorio.jaEm', { tecla: ja }) : undefined} />
  ), { toque: true });
  const trancada = estado === 'longe' || estado === 'indisponivel';
  const nome = nomeDaMagia(magia.id);
  const efeito = textoDoEfeito(efeitoDe(magia));
  const pct = chance == null ? null : Math.round(chance * 100);
  const rotulo = trancada
    ? t('pk.trancada', { magia: nome, skill: nomeDaSkill('magery'), min: umaCasa(magia.skillMin) })
    : [nome, t('magiaUi.custoMana', { mana: magia.mana }), efeito, pct == null ? null : t('grimorio.chance', { pct })].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      className={`pk-t${trancada ? ' is-trancada' : ''}${atual ? ' is-atual' : ''}`}
      aria-disabled={trancada || undefined}
      aria-label={rotulo + (ja ? ` · ${t('grimorio.jaEm', { tecla: ja })}` : '')}
      {...dica}
      onClick={() => { if (!trancada) onPick(magia.id); }}
    >
      <img src={iconeDaMagia(magia.id)} alt="" draggable={false} />
      {trancada
        ? <span className="pk-t__m pk-t__m--tr"><IcCadeado />{Math.ceil(magia.skillMin)}</span>
        : <span className="pk-t__m">{magia.mana}</span>}
      {ja && <span className="pk-t__k">{ja}</span>}
      <span className="pk-t__n">{nome}</span>
      {!trancada && chance != null && (
        <span className="pk-t__c"><i style={{ width: `${chance * 100}%`, background: corChance(chance) }} /></span>
      )}
    </button>
  );
}

function TileItem({ item, vip, ja, atual, onPick }: { item: ItemDoGrimorio; vip: boolean; ja: string; atual: boolean; onPick(id: string): void }) {
  const nome = nomeDoItem(item.id);
  const tem = item.gratis || item.qtd > 0;
  const estoque = item.gratis ? t('grimorio.item.sempre')
    : item.qtd > 0 ? t('grimorio.item.naMochila', { n: item.qtd })
      : vip ? t('pk.vipSemEstoque') : t('pk.acabou');
  return (
    <button
      type="button"
      className={`pk-t${atual ? ' is-atual' : ''}`}
      title={`${nome} · ${estoque}`}
      aria-label={`${nome} · ${estoque}${ja ? ` · ${t('grimorio.jaEm', { tecla: ja })}` : ''}`}
      onClick={() => onPick(item.id)}
    >
      <img src={iconePara(item.id)} alt="" draggable={false} />
      {!item.gratis && (
        <span className={`pk-t__m pk-t__m--q${!tem && vip ? ' is-vip' : ''}`}>
          {tem ? `×${numero(item.qtd)}` : vip ? '∞' : '×0'}
        </span>
      )}
      {ja && <span className="pk-t__k">{ja}</span>}
      <span className="pk-t__n">{nome}</span>
    </button>
  );
}
